import { Box, IconButton } from "@mui/material"
import { DraftOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import { TransferRowActions } from "../components/TransferListPage/TransferRowActions"
import { TransferStatusCell } from "../components/TransferListPage/TransferStatusCell"
import { TransferTypeChip } from "../components/TransferTypeChip"

import type { Transfer } from "@/graphql/codegen/graphql"
import { formatDate } from "@/utils/date"
import { formatUserName } from "@/utils/user"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface UseTransferColumnsProps {
  isPrinting: boolean
  onEdit: (transfer: Transfer) => void
  onCancel: (transfer: Transfer) => void
  onConfirmWorkOrder: (transfer: Transfer) => void
  onPrintWorkOrder: (transfer: Transfer) => void
  onPalletLabelPrintAll: (transfer: Transfer) => void
  onDocumentsOpen: (transfer: Transfer) => void
}

export const useTransferColumns = ({
  isPrinting,
  onEdit,
  onCancel,
  onConfirmWorkOrder,
  onPrintWorkOrder,
  onPalletLabelPrintAll,
  onDocumentsOpen,
}: UseTransferColumnsProps): ColumnDef<Transfer>[] => [
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ row }) => <TransferTypeChip type={row.original.transferType} />,
    enableSorting: true,
  },
  {
    accessorKey: "referenceCode",
    header: "Transfer #",
    enableSorting: false,
  },
  {
    accessorKey: "sourceWarehouse",
    header: "From",
    cell: ({ row }) => formatWarehouseLabel(row.original.sourceWarehouseArea),
    enableSorting: true,
  },
  {
    accessorKey: "targetWarehouse",
    header: "To",
    cell: ({ row }) => formatWarehouseLabel(row.original.targetWarehouseArea),
    enableSorting: true,
  },
  {
    accessorKey: "createdOn",
    header: "Created",
    cell: ({ row }) => formatDate(row.original.createdOn),
    enableSorting: true,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <TransferStatusCell status={row.original.status} as400SyncStatus={row.original.as400SyncStatus} />
    ),
    enableSorting: true,
  },
  {
    accessorKey: "createdBy",
    header: "Created By",
    cell: ({ row }) => formatUserName(row.original.createdByUser, true),
    enableSorting: true,
  },
  {
    accessorKey: "productCostCompleted",
    header: "Product Cost Completed",
    cell: () => "N/A",
    enableSorting: false,
  },
  {
    accessorKey: "docs",
    header: "",
    size: 56,
    enableSorting: false,
    cell: ({ row }) =>
      row.original.hasDocuments ? (
        <Box className='flex items-center justify-center'>
          <IconButton size='small' onClick={() => onDocumentsOpen(row.original)}>
            <DraftOutlined />
          </IconButton>
        </Box>
      ) : null,
  },
  {
    accessorKey: "actions",
    header: "",
    size: 112,
    enableSorting: false,
    cell: ({ row }) => (
      <Box className='flex items-center justify-end gap-x-2'>
        <TransferRowActions
          transfer={row.original}
          isPrinting={isPrinting}
          onEdit={onEdit}
          onCancel={onCancel}
          onConfirmWorkOrder={onConfirmWorkOrder}
          onPrintWorkOrder={onPrintWorkOrder}
          onPalletLabelPrintAll={onPalletLabelPrintAll}
        />
        <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
          {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>
    ),
  },
]
